import { DecimalPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, Input } from '@angular/core';
import {
	ENTITY_COLUMN_KEY,
	TableWidget
} from '../../../../models/preview/preview.models';

/**
 * The `table` widget kind, which the registry has been resolving to a
 * placeholder since the preview was written.
 *
 * Every other kind had a component; this one did not, so a slide asking for a
 * table got a grey box. The builder makes that visible — "top voices" and "top
 * sites" are tables by nature — so it is filled in here rather than worked
 * around by drawing a bar chart of a list.
 *
 * Numbers are right-aligned and thousands-separated; text is left alone. The
 * column tells us which is which (`kind`), because a handle like `@2024news`
 * is text however much it looks like a number.
 *
 * Header and body are driven off the SAME column list and nothing here names a
 * column, so a table that grows an `Entity` column when it holds two brands
 * renders it with no change — as the ordinary left-aligned text its `kind`
 * asks for. What such a table does need is the rule between the two brands'
 * rows; see `computeGroupStarts`.
 */
@Component({
	standalone: true,
	selector: 'app-table-widget',
	// NgIf is load-bearing: without it the `*ngIf` below builds an
	// `<ng-template>` that nothing ever instantiates, so the component mounts
	// and draws NOTHING. The compiler says so only as a warning among hundreds,
	// and the table looked like a backend that returned no rows.
	imports: [NgIf, NgForOf, NgClass, DecimalPipe],
	template: `
		<table class="report-table" *ngIf="widget">
			<thead>
				<tr>
					<th
						*ngFor="let column of widget.content.columns"
						[ngClass]="{ 'is-number': column.kind === 'number' }"
					>
						{{ column.label }}
					</th>
				</tr>
			</thead>
			<tbody>
				<tr
					*ngFor="let row of widget.content.rows; let index = index"
					[ngClass]="{ 'is-group-start': groupStarts.has(index) }"
				>
					<td
						*ngFor="let column of widget.content.columns"
						[ngClass]="{ 'is-number': column.kind === 'number' }"
					>
						<!-- A value that could not be read is a dash, never a
						     blank a reader takes for zero. -->
						{{
							column.kind !== 'number'
								? row[column.key]
								: row[column.key] == null
								? '—'
								: ($any(row[column.key]) | number)
						}}
					</td>
				</tr>
			</tbody>
		</table>
	`,
	styles: [
		`
			:host {
				display: block;
				width: 100%;
			}

			.report-table {
				width: 100%;
				border-collapse: collapse;
				font-size: 0.8125rem;
				line-height: 1.25rem;
			}

			th,
			td {
				padding: 0.5rem 0.75rem;
				text-align: left;
				/* A global .row rule turns table rows into flex containers
				   elsewhere in this app; keeping the cells styled here rather
				   than through shared classes is what stops that. */
				border-bottom: 1px solid
					var(--report-divider, rgba(0, 0, 0, 0.06));
			}

			th {
				font-weight: 600;
				color: var(--report-value, #333a42);
				opacity: 0.6;
				text-transform: uppercase;
				letter-spacing: 0.02em;
				font-size: 0.6875rem;
			}

			td {
				color: var(--report-value, #333a42);
			}

			tbody tr:last-child td {
				border-bottom: none;
			}

			/* Darker than the row rule, so the seam between two entities reads
			   as a break in the list rather than as one more row of it. */
			tbody tr.is-group-start td {
				border-top: 1px solid var(--report-border, rgba(0, 0, 0, 0.16));
			}

			.is-number {
				text-align: right;
				font-variant-numeric: tabular-nums;
			}
		`
	]
})
export class TableWidgetComponent {
	@Input() set widget(value: TableWidget) {
		this._widget = value;
		this.groupStarts = computeGroupStarts(value);
	}

	get widget(): TableWidget {
		return this._widget;
	}

	private _widget!: TableWidget;

	/** Row indexes that open a new entity's list. Empty for an ordinary table. */
	groupStarts = new Set<number>();
}

/**
 * Where one entity's rows end and the next entity's begin.
 *
 * A compared table is each entity's own top ten CONCATENATED, not a ranking of
 * the twenty together — the backend refuses to re-rank two already-truncated
 * lists, and it is right to. Drawn flat, that seam is invisible: the reader
 * sees a twenty-row ranking that is not one, and the eleventh row looks like it
 * lost to the tenth when it was in fact winning a different contest. A rule
 * above each new entity is the cheapest thing that says so.
 *
 * Nothing is reordered or regrouped. The rule is drawn where the data ALREADY
 * changed, so a table the backend chose to order some other way still renders
 * in the order it arrived.
 *
 * Gated on the `entity` COLUMN rather than on a repeated first value, so an
 * ordinary table that happens to repeat one is not chopped into sections.
 */
function computeGroupStarts(widget: TableWidget): Set<number> {
	const starts = new Set<number>();
	const columns = widget?.content?.columns ?? [];
	if (!columns.some(column => column.key === ENTITY_COLUMN_KEY)) {
		return starts;
	}

	let previous: string | null = null;
	(widget.content.rows ?? []).forEach((row, index) => {
		const entity = String(row[ENTITY_COLUMN_KEY] ?? '');
		if (index > 0 && entity !== previous) {
			starts.add(index);
		}
		previous = entity;
	});
	return starts;
}
